import { ComponentRef, Injectable, Type, ViewContainerRef } from '@angular/core';
import { ModalAtomComponent } from '@app/components/atoms/modal-atom/modal-atom.component';
import { EMPTY_STRING } from '@app/constants';
import { BehaviorSubject } from 'rxjs';

export interface ModalHeaderActionsConfig {
  component: Type<unknown>;
  props?: Record<string, unknown>;
  outputs?: Record<string, (value: unknown) => void>;
}

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private static readonly DEFAULT_MODAL_CONFIG = { showCloseButton: true, isRightAligned: false };

  private readonly isOpenSubject = new BehaviorSubject<boolean>(false);
  private readonly titleSubject = new BehaviorSubject<string>(EMPTY_STRING);
  private readonly isBoldSubject = new BehaviorSubject<boolean>(false);
  private readonly isCenteredSubject = new BehaviorSubject<boolean>(false);
  private readonly showSeparatorSubject = new BehaviorSubject<boolean>(false);
  private readonly showCloseButtonSubject = new BehaviorSubject<boolean>(true);
  private readonly isRightAlignedSubject = new BehaviorSubject<boolean>(false);
  private readonly headerActionsSubject = new BehaviorSubject<ModalHeaderActionsConfig | null>(
    null
  );
  private modalRef: ComponentRef<ModalAtomComponent> | null = null;
  private viewContainerRef: ViewContainerRef | null = null;

  isOpen$ = this.isOpenSubject.asObservable();
  title$ = this.titleSubject.asObservable();
  isBold$ = this.isBoldSubject.asObservable();
  isCentered$ = this.isCenteredSubject.asObservable();
  showSeparator$ = this.showSeparatorSubject.asObservable();
  showCloseButton$ = this.showCloseButtonSubject.asObservable();
  isRightAligned$ = this.isRightAlignedSubject.asObservable();
  headerActions$ = this.headerActionsSubject.asObservable();

  updateHeader(title: string, isBold = false, isCentered = false, showSeparator = false) {
    this.titleSubject.next(title);
    this.isBoldSubject.next(isBold);
    this.isCenteredSubject.next(isCentered);
    this.showSeparatorSubject.next(showSeparator);
  }

  setHeaderActions(config: ModalHeaderActionsConfig | null) {
    this.headerActionsSubject.next(config);
  }

  createComponentIfDoesntExist(viewContainerRef: ViewContainerRef) {
    if (this.modalRef) return;
    this.modalRef = viewContainerRef.createComponent(ModalAtomComponent);
  }

  clearViewContainerRef() {
    this.viewContainerRef = null;
  }

  clearModalRef() {
    this.modalRef = null;
  }

  loadViewContainerRef(viewContainerRef: ViewContainerRef) {
    this.viewContainerRef = viewContainerRef;
  }

  loadComponent<T>(component: Type<T>, props: Record<string, unknown> = {}, className?: string) {
    if (!this.viewContainerRef) return;
    this.createComponentIfDoesntExist(this.viewContainerRef);
    if (!this.modalRef) return;
    const componentRef = this.modalRef.instance.loadComponent(component, props, className);
    return componentRef;
  }

  open(config?: { showCloseButton: boolean; isRightAligned?: boolean }) {
    const modalConfig = config ?? ModalService.DEFAULT_MODAL_CONFIG;
    this.showCloseButtonSubject.next(modalConfig.showCloseButton);
    this.isRightAlignedSubject.next(modalConfig.isRightAligned ?? false);
    this.isOpenSubject.next(true);
  }

  close() {
    this.isOpenSubject.next(false);
    this.showCloseButtonSubject.next(true);
    this.headerActionsSubject.next(null);
  }
}
